import { useState } from "react";
import { PanelRightClose } from "lucide-react";
import { ENC } from "@/constants/testIds";
import EncounterDiagnosisWorkspace from "@/components/encounter/EncounterDiagnosisWorkspace";
import EncounterCptWorkspace from "@/components/encounter/EncounterCptWorkspace";
import EncounterOrderPanel from "@/components/encounter/EncounterOrderPanel";

const TABS = [
  { id: "diagnosis", label: "Diagnosis" },
  { id: "cpt", label: "CPT Code" },
  { id: "order", label: "Order" },
];

function CodingEmptyHint({ text }) {
  return (
    <p data-testid={ENC.screenCodingEmpty} className="emr-encdetail__coding-empty">
      {text}
    </p>
  );
}

/**
 * Right-side collapsible panel: Diagnosis, CPT Code, and Order tabs. Diagnosis
 * and CPT reuse the existing EncounterDiagnosisWorkspace/EncounterCptWorkspace
 * components as-is (same props, same Note-scoped data and handlers they
 * already had inline next to the Note editor) — only where they render has
 * moved. Order has no backend yet, so EncounterOrderPanel is a UI shell.
 */
export default function EncounterCodingPanel({
  onCollapse,
  diagnosisAvailable,
  diagnosis,
  cptAvailable,
  cpt,
  orderReadOnly,
  emptyHint,
}) {
  const [tab, setTab] = useState("diagnosis");

  return (
    <div data-testid={ENC.screenCodingPanel} className="emr-encdetail__coding">
      <div className="emr-encdetail__coding-tabs-row">
        <div
          data-testid={ENC.screenCodingTabs}
          className="emr-encdetail__coding-tabs"
          role="tablist"
          aria-label="Diagnosis, CPT Code, and Order"
        >
          {TABS.map((item) => {
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`${ENC.screenCodingTab}-${item.id}`}
                onClick={() => setTab(item.id)}
                className={`emr-encdetail__coding-tab${active ? " emr-encdetail__coding-tab--active" : ""}`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        {onCollapse ? (
          <button
            type="button"
            data-testid={ENC.screenCodingCollapse}
            onClick={onCollapse}
            className="emr-encdetail__chart-collapse-btn"
            aria-label="Collapse Diagnosis, CPT, and Orders"
            title="Collapse Diagnosis, CPT, and Orders"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <div className="emr-encdetail__coding-body">
        {tab === "diagnosis" ? (
          diagnosisAvailable ? <EncounterDiagnosisWorkspace {...diagnosis} /> : <CodingEmptyHint text={emptyHint} />
        ) : null}
        {tab === "cpt" ? (
          cptAvailable ? <EncounterCptWorkspace {...cpt} /> : <CodingEmptyHint text={emptyHint} />
        ) : null}
        {tab === "order" ? <EncounterOrderPanel readOnly={orderReadOnly} /> : null}
      </div>
    </div>
  );
}
